// src/components/LanguageSwitcher.tsx
"use client";

import classNames from "classnames";
import { ChevronDown } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import React, { useCallback, useMemo } from "react";

import {
  useDirection,
  useLanguage as useLang,
  useSetLanguage,
} from "@/context/TranslationsContext";
import {
  defaultLanguage,
  type Language,
  supportedLanguages,
} from "@/translations/supportedLanguages";

const LanguageSwitcher: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();

  const currentLang = useLang();
  const setLanguage = useSetLanguage();
  const direction = useDirection();
  const isRTL = direction === "rtl";

  /** Split pathname *once* */
  const { pathSegments, isSupportedLang } = useMemo(() => {
    const segments = pathname.split("/").filter(Boolean);
    const supported = supportedLanguages.some((l) => l.code === segments[0]);
    return { pathSegments: segments, isSupportedLang: supported };
  }, [pathname]);

  /** Stable handler */
  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedLang = e.target.value as Language;
      let newSegs = [...pathSegments];

      if (selectedLang === defaultLanguage.code) {
        if (isSupportedLang) newSegs.shift();
      } else {
        if (isSupportedLang) newSegs[0] = selectedLang;
        else newSegs.unshift(selectedLang);
      }

      setLanguage(selectedLang);
      // navigate to the new locale path…
      await router.push(`/${newSegs.join("/")}`);
      // …then immediately refresh to fetch the updated RSC payload
      router.refresh();
    },
    [isSupportedLang, pathSegments, router, setLanguage],
  );

  const options = useMemo(
    () =>
      [defaultLanguage, ...supportedLanguages].map((lang) => (
        <option key={lang.code} value={lang.code}>
          {lang.name}
        </option>
      )),
    [],
  );

  return (
    <div className="relative w-40 min-w-[8rem]">
      <select
        onChange={handleChange}
        value={currentLang}
        className={classNames(
          "h-11 w-full appearance-none rounded-lg border bg-white text-sm sm:text-base text-black shadow-sm",
          "focus:outline-none focus:ring-2 focus:ring-primary",
          "px-4 pr-10",
          {
            "text-left pr-10 pl-4": !isRTL,
            "text-right pl-10 pr-4": isRTL,
          },
        )}
        aria-label="Select Language"
        dir={isRTL ? "rtl" : "ltr"}
      >
        {options}
      </select>

      <ChevronDown
        className={classNames(
          "absolute top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none",
          { "right-3": !isRTL, "left-3": isRTL },
        )}
        size={18}
      />
    </div>
  );
};

export default LanguageSwitcher;
